/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { Table } from '@/components/tables/table';
import { TableColumnsType, Typography } from 'antd';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useState } from 'react';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import {
  getListActivityLogsUserClient,
  watchListActivityLogsUserClient,
  watchClientUserLoading,
  watchClientUserPagination
} from '@/redux/slices/client.slice';
import { formatDateTime } from '@/utils/common';
import { tableScrollY } from '@/utils/common';

type Filter = {
  textSearch?: string;
};

type PageChangeParams = {
  page?: number;
  pageSize?: number;
  filter?: Filter;
};

export default function UserActivityLog() {
  const dispatch = useAppDispatch();

  const listActivityLogsUserClient = useAppSelector(
    watchListActivityLogsUserClient
  );
  const pagination = useAppSelector(watchClientUserPagination);
  const loading = useAppSelector(watchClientUserLoading);

  const [filter, setFilter] = useState<Filter>({});

  const handlePageChange = useCallback(
    ({
      page = PAGINATION_PARAMS.offset,
      pageSize = PAGINATION_PARAMS.limit,
      filter
    }: PageChangeParams) => {
      dispatch(
        getListActivityLogsUserClient({
          offset: page,
          limit: pageSize,
          search: filter?.textSearch
        })
      );
    },
    [dispatch]
  );

  const handleSearch = (values: Filter) => {
    const newFilter: Filter = {
      ...filter,
      ...values
    };
    setFilter(newFilter);
    handlePageChange({ filter: newFilter });
  };

  const handleReset = () => {
    handlePageChange({ filter });
  };

  useEffect(() => {
    handlePageChange({});
  }, [handlePageChange]);

  const columns: TableColumnsType<ActivityLog> = [
    {
      title: 'User ID',
      width: 150,
      dataIndex: 'userIdMnmn',
      key: 'userIdMnmn',
      fixed: 'left'
    },
    {
      title: 'Tên hiển thị',
      width: 150,
      dataIndex: 'fullName',
      key: 'fullName',
      fixed: 'left'
    },
    {
      title: 'Hoạt động',
      dataIndex: 'action',
      key: 'action',
      width: 150
    },
    {
      title: 'Thời gian',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 150,
      render: (record) => formatDateTime(record)
    },
    {
      title: 'Thiết bị',
      dataIndex: 'device',
      key: 'device',
      width: 150
    }
  ];

  return (
    <div css={rootStyles}>
      <Typography.Title level={5}>
        Ghi lại hoạt động người dùng (User Activity Log)
      </Typography.Title>
      <Table<ActivityLog>
        rowKey={(record) => record.createdDate}
        columns={columns}
        dataSource={listActivityLogsUserClient}
        scrollY={tableScrollY(pagination.limitPage)}
        placeholder='Nhập từ khoá'
        loading={loading}
        pagination={{
          showQuickJumper: true,
          current: pagination.curPage,
          pageSize: pagination.limitPage,
          total: pagination.totalRows,
          onChange: (page, pageSize) => {
            handlePageChange({ page, pageSize, filter });
          }
        }}
        totalResult={pagination.totalRows}
        onFilter={handleSearch}
        onReset={handleReset}
      />
    </div>
  );
}

const rootStyles = css`
  padding: 2rem 2.4rem;
`;
